// Help Content Registry
//
// Content lives in per-entry markdown files under ./articles and ./contextual,
// compiled into ./generated.ts by scripts/help-content/generate.mjs (runs via
// npm pre-hooks: prebuild / pretest / pretype-check / predev).
//
// To add or edit help content, edit the markdown files — never generated.ts.
// See Documentation/HELP_CONTENT_GUIDE.md.

import { generatedArticles, generatedContextual } from './generated';

export interface HelpArticle {
  slug: string;
  title: string;
  description: string;
  category: HelpCategory;
  icon?: string;
  /** Date the content last changed (from frontmatter) */
  updated?: string;
  /** Date the content was last confirmed accurate against the product */
  verified?: string;
  content: string;
}

export type HelpCategory = 'getting-started' | 'workspaces' | 'legislation' | 'calendar' | 'outreach' | 'reports' | 'directories' | 'general';

export interface CategoryInfo {
  id: HelpCategory;
  name: string;
  description: string;
  order: number;
}

// NOTE: keep in sync with VALID_CATEGORIES in scripts/help-content/generate.mjs
export const categories: CategoryInfo[] = [
  {
    id: 'getting-started',
    name: 'Getting Started',
    description: 'Learn the basics of Capitol Commons',
    order: 1
  },
  {
    id: 'workspaces',
    name: 'Workspaces',
    description: 'Managing workspaces and tracking bills',
    order: 2
  },
  {
    id: 'legislation',
    name: 'Legislation & Budget',
    description: 'Browsing bills, legislators, and budget data',
    order: 3
  },
  {
    id: 'calendar',
    name: 'Calendar & Events',
    description: 'Legislative calendar and event tracking',
    order: 4
  },
  {
    id: 'outreach',
    name: 'Outreach',
    description: 'Creating surveys, campaigns, and public engagement tools',
    order: 5
  },
  {
    id: 'reports',
    name: 'Reports',
    description: 'Building and sharing reports',
    order: 6
  },
  {
    id: 'directories',
    name: 'Directories & Data',
    description: 'Lobbying, campaign finance, and non-profit directories',
    order: 7
  },
  {
    id: 'general',
    name: 'General',
    description: 'Account, organization, and other settings',
    order: 8
  }
];

const categoryOrder = new Map(categories.map(c => [c.id, c.order]));

// Articles ordered by category, then title, for a stable Help Center listing
export const helpArticles: HelpArticle[] = (generatedArticles as HelpArticle[])
  .slice()
  .sort((a, b) =>
    (categoryOrder.get(a.category) ?? 99) - (categoryOrder.get(b.category) ?? 99) ||
    a.title.localeCompare(b.title)
  );

// Contextual help content - short snippets for UI elements
export const contextualContent: Record<string, { title: string; content: string; updated?: string; verified?: string }> =
  Object.fromEntries(
    generatedContextual.map(c => [c.key, { title: c.title, content: c.content, updated: c.updated, verified: c.verified }])
  );

// Helper functions
export function getArticleBySlug(slug: string): HelpArticle | undefined {
  return helpArticles.find(article => article.slug === slug);
}

export function getArticlesByCategory(category: HelpCategory): HelpArticle[] {
  return helpArticles.filter(article => article.category === category);
}

export function getContextualContent(key: string): { title: string; content: string } | undefined {
  return contextualContent[key];
}

export function searchArticles(query: string): HelpArticle[] {
  const lowerQuery = query.toLowerCase();
  return helpArticles.filter(article =>
    article.title.toLowerCase().includes(lowerQuery) ||
    article.description.toLowerCase().includes(lowerQuery) ||
    article.content.toLowerCase().includes(lowerQuery)
  );
}
